'use client';

import React, { useMemo } from 'react';
import { type RecordAny, type SurfaceComponent } from '@macaron-a2ui/core/v0_8';
import { useMacaronA2UIActions, useMacaronA2UIRenderComponent, useMacaronA2UIState } from './context';

interface MacaronA2UISurfaceProps {
  fallback?: React.ReactNode;
  className?: string;
  wrapRoot?: (root: React.ReactNode, components: SurfaceComponent[]) => React.ReactNode;
}

export function MacaronA2UISurface({ fallback = null, className, wrapRoot }: MacaronA2UISurfaceProps) {
  const { root, components, data } = useMacaronA2UIState();
  const actions = useMacaronA2UIActions();
  const renderComponent = useMacaronA2UIRenderComponent();
  const componentMap = useMemo(() => new Map(components.map((item) => [item.id, item])), [components]);
  const getComponentEntry = (id: string) => {
    const node = componentMap.get(id);
    if (!node || !node.component || typeof node.component !== 'object') return undefined;
    const entry = Object.entries(node.component)[0];
    if (!entry) return undefined;
    const [type, configRaw] = entry;
    return { type, config: (configRaw || {}) as RecordAny, weight: typeof node.weight === 'number' ? node.weight : undefined };
  };
  const renderById = (id: string): React.ReactNode => {
    const entry = getComponentEntry(id);
    if (!entry) return null;
    return renderComponent(entry.type, entry.config, {
      data,
      updateData: actions.updateData,
      renderChild: renderById,
      getComponentType: (childId) => getComponentEntry(childId)?.type,
      getComponentConfig: (childId) => getComponentEntry(childId)?.config,
      getComponentWeight: (childId) => getComponentEntry(childId)?.weight,
      onAction: actions.dispatchAction,
      onLocalHandler: actions.dispatchLocalHandler,
      componentId: id,
    }) as React.ReactNode;
  };
  const renderedRoot = renderById(root);
  if (!renderedRoot) return <>{fallback}</>;
  const body = wrapRoot ? wrapRoot(renderedRoot, components) : renderedRoot;
  return className ? <div className={className}>{body}</div> : <>{body}</>;
}
